import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Input, Select } from 'common-src/components/base';
import { getPatient } from 'common-src/models/Patient';
import PatientIntegrationsAthena, {
  AthenaDepartmentsOptions,
  AthenaPracticeOptions,
  createPatientIntegrationsAthena,
  getPatientFromAthena,
  updatePatientIntegrationsAthena,
  validatePatientIntegrationsAthena,
} from 'common-src/models/PatientIntegrationsAthena';
import { ColorsNew } from 'common-src/styles';

import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import { initialState } from './constants';
import { catchErrors } from './helpers';
import styles from './LinkAthenaPatientPopup.module.scss';

const LinkAthenaPatientPopup = ({ onClose, patientId, integration }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(initialState);
  const patient = useSelector((state) => getPatient(state, patientId));

  const { errors, setErrors, renderButtons } = usePopup();

  useEffect(() => {
    const newData = integration
      ? _.pick(integration, Object.keys(initialState))
      : { ...initialState };

    setData(newData);
  }, [integration]);

  const onChangeHandler = (field, value) => {
    setErrors((prev) => ({ ...prev, ...validatePatientIntegrationsAthena(field, value) }));
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const validatePatientMatch = (athenaPatientData) => {
    if (!patient || !athenaPatientData) {
      return { isValid: false, error: 'Unable to retrieve patient information.' };
    }

    const normalizeString = (str) => (str || '').trim().toLowerCase();
    const normalizeDate = (date) => {
      if (!date) return '';
      const momentDate = moment(date);
      return momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : '';
    };

    const localFirstName = normalizeString(patient.firstName);
    const localLastName = normalizeString(patient.lastName);
    const localDob = normalizeDate(patient.dob);

    const athenaFirstName = normalizeString(
      athenaPatientData.firstname || athenaPatientData.firstName,
    );
    const athenaLastName = normalizeString(
      athenaPatientData.lastname || athenaPatientData.lastName,
    );
    const athenaDob = normalizeDate(athenaPatientData.dob);

    const errors = [];
    if (localFirstName !== athenaFirstName) {
      errors.push(
        `First name mismatch: Local "${patient.firstName}" vs Athena "${athenaPatientData.firstname || athenaPatientData.firstName}"`,
      );
    }
    if (localLastName !== athenaLastName) {
      errors.push(
        `Last name mismatch: Local "${patient.lastName}" vs Athena "${athenaPatientData.lastname || athenaPatientData.lastName}"`,
      );
    }
    if (localDob !== athenaDob) {
      errors.push(`Date of birth mismatch: Local "${localDob}" vs Athena "${athenaDob}"`);
    }

    if (errors.length > 0) {
      return {
        isValid: false,
        error: `Patient information does not match:\n${errors.join('\n')}`,
      };
    }

    return { isValid: true };
  };

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback) => {
    const { errors, hasErrors } = catchErrors(data);
    if (hasErrors) {
      setErrors(errors);
      return;
    }

    loadingCallback();

    if (integration?.id) {
      dispatch(
        updatePatientIntegrationsAthena(
          { id: integration.id, ...data },
          {
            successBlock: () => {
              successCallback('Athena patient updated!');
              onClose(true);
            },
            errorBlock: (err) => errorCallback(err),
          },
        ),
      );

      return;
    }

    dispatch(
      getPatientFromAthena(data.athenaPatientId, data.practiceId, {
        successBlock: (athenaPatient) => {
          if (!athenaPatient) {
            errorCallback('Patient not found in Athena with the provided ID.');
            return;
          }

          const validationResult = validatePatientMatch(athenaPatient);
          if (!validationResult.isValid) {
            errorCallback(validationResult.error);
            return;
          }

          dispatch(
            createPatientIntegrationsAthena(
              { patientId, ...data },
              {
                successBlock: () => {
                  successCallback('Athena patient linked!');
                  onClose(true);
                },
                errorBlock: (err) => errorCallback(err),
              },
            ),
          );
        },
        errorBlock: (err) => {
          console.error('Error fetching Athena patient:', err);
          errorCallback(err?.message || 'Failed to fetch Athena patient information.');
        },
      }),
    );
  };

  return (
    <BasePopup
      id="link-athena-patient"
      open
      onClose={onClose}
      title={`${integration ? 'Edit' : 'Link'} Athena Patient`}
    >
      <div className={[styles.container, 'flex-column', 'gap-16'].join(' ')}>
        <Select
          id="practice-id"
          label="Practice ID"
          options={AthenaPracticeOptions}
          onChange={(op) => onChangeHandler('practiceId', op?.value ?? null)}
          value={AthenaPracticeOptions.find((op) => op.value === data.practiceId)}
          placeholder="Select practice ID"
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.practiceId}
        />
        <Input
          id="athena-patient-id"
          placeholder="Enter athena patient id"
          label="Athena Patient ID"
          value={data.athenaPatientId}
          onTextChange={(value) => onChangeHandler('athenaPatientId', value)}
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.athenaPatientId}
        />
        <Select
          id="department"
          label="Department"
          options={AthenaDepartmentsOptions}
          onChange={(op) => onChangeHandler('department', op?.value ?? null)}
          value={AthenaDepartmentsOptions.find((op) => op.value === data.department)}
          placeholder="Select department"
          isClearable
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.department}
          disabled={!!integration}
        />
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit: onSubmitHandler,
        isSubmitEnabled: !Object.values(errors).some((e) => e),
        submitButtonText: 'Save and Close',
      })}
    </BasePopup>
  );
};

LinkAthenaPatientPopup.propTypes = {
  onClose: PropTypes.func,
  patientId: PropTypes.number,
  integration: PropTypes.exact(PatientIntegrationsAthena.schema),
};

export default LinkAthenaPatientPopup;
